import { useState } from 'react'
import type { Pronunciation } from '@super/shared-types'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { Pencil, Trash2, Link2, Volume2 } from 'lucide-react'
import { toast } from 'sonner'
import { knowledgeApiClient } from '@/lib/api'
import { useAppAuth } from '@/contexts/app-auth.context'

interface PronunciationGuidesTabProps {
  pronunciations: Pronunciation[]
  loading: boolean
  onEdit: (pronunciation: Pronunciation) => void
  onReload: () => void
}

export function PronunciationGuidesTab({
  pronunciations,
  loading,
  onEdit,
  onReload,
}: PronunciationGuidesTabProps) {
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [deleting, setDeleting] = useState<string | null>(null)

  const handleDelete = async (pronunciation: Pronunciation) => {
    if (!currentCompany?.id) return

    if (
      !confirm(
        `Are you sure you want to delete the pronunciation for "${pronunciation.word}"?`,
      )
    ) {
      return
    }

    if (!pronunciation.id) {
      toast.error('Pronunciation ID is missing')
      return
    }

    try {
      setDeleting(pronunciation.id)
      if (isSuperAdmin) {
        await knowledgeApiClient.deletePronunciationForCompany(
          pronunciation.id,
          currentCompany.id,
        )
      } else {
        await knowledgeApiClient.deletePronunciation(pronunciation.id)
      }
      toast.success('Pronunciation deleted successfully')
      onReload()
    } catch (error) {
      console.error('Error deleting pronunciation:', error)
      toast.error('Failed to delete pronunciation')
    } finally {
      setDeleting(null)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Word</TableHead>
                <TableHead>Phonetic Spelling</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-16" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  if (pronunciations.length === 0) {
    return (
      <EmptyState
        icon={<Volume2 className="h-8 w-8" />}
        title="No pronunciation guides"
        description="Have pronounced terms and acronyms be music to your&nbsp;ears."
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Word / Acronym / Url</TableHead>
              <TableHead>Phonetic Spelling</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pronunciations.map(pronunciation => {
              const isLinkedToResource =
                pronunciation.links && pronunciation.links.length > 0

              return (
                <TableRow key={pronunciation.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span>{pronunciation.word}</span>
                      {isLinkedToResource && (
                        <Badge variant="secondary" className="text-xs gap-1">
                          <Link2 className="h-3 w-3" />
                          Linked
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {pronunciation.phoneme}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(pronunciation)}
                        title={
                          isLinkedToResource
                            ? 'Edit pronunciation (word cannot be changed)'
                            : 'Edit pronunciation'
                        }
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(pronunciation)}
                        disabled={deleting === pronunciation.id}
                        title="Delete pronunciation"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
